<template>
  <div>
    <div
      v-for="(payment, indexPayment) in visiblePayments"
      :key="`payment-account-number-${indexPayment}`"
      class="bg-secondary-13 p-3 rounded mt-5"
    >
      <div>
        <div class="row">
          <div class="col">
            <icon-view
              icon="fa fa-usd-square"
              icon-background="#2DB782"
              icon-color="#fff"
              title="Pago Parcial"
            />
          </div>
          <div
            v-if="!hasDebtPurchaseOrderNumber(payment)"
            class="col text-center"
          >
            <div class="color-black font-16 font-weight-bold">
              {{ payment.paymentNumber }}
            </div>
          </div>
          <div class="col d-flex justify-content-end">
            <div class="d-flex">
              <span>Monto Total:</span>
              <span class="color-black font-weight-bold ml-2">
                {{ payment.purchaseTotal | price }}
              </span>
            </div>
          </div>
          <div
            v-if="!hasDebtPurchaseOrderNumber(payment)"
            class="col-auto d-flex align-items-end justify-content-end"
          >
            <action-button
              v-if="cantPrintDistributionNumber && payment.paymentId"
              i="fas fa-print"
              variant="success"
              @click="$emit('print-payment-distribution', payment.paymentId)"
            >
              Imprimir Distribución de Pago
            </action-button>
          </div>
          <div
            v-if="!hasDebtPurchaseOrderNumber(payment)"
            class="col-1 d-flex align-items-end justify-content-end"
          >
            <action-button
              v-if="
                showUpdateVoucherStatus &&
                  payment.voucherNumber &&
                  (!payment.voucherTrackingInfo ||
                    !payment.voucherTrackingInfo.cashClearedDate)
              "
              class="btn outline-success py-1 px-0 pl-2 d-flex justify-content-center align-items-center"
              i="fas fa-redo-alt"
              @click="$emit('on-update-voucher-status', payment.voucherNumber)"
            />
          </div>
        </div>
        <div
          v-if="!payment.isDebtVoucher && !hasDebtPurchaseOrderNumber(payment)"
          class="row"
        >
          <div class="col ml-2 mt-2">
            <span class="color-gray-6 font-16 font-weight-normal mr-2">
              PO ID:
            </span>
            <custom-drop-down
              v-if="showChoosePO(payment)"
              v-model="value.purchaseOrderId"
              class="w-50"
              :clearable="false"
              fieldkey="id"
              fieldname="name"
              :options="purchaseOrders"
              placeholder="Seleccione"
              @input="onChangePurchaseOrderHandler"
            />
            <span v-else class="color-black font-16 font-weight-normal">
              {{
                `${payment.purchaseOrderId} (${payment.purchaseOrderBudgetYear})`
              }}
            </span>
          </div>
          <div class="col d-flex align-items-center justify-content-center">
            <span class="color-gray-6 font-16 font-weight-normal mr-2">
              Voucher ID:
            </span>
            <span class="color-black font-16 font-weight-normal">
              {{ payment.voucherNumber | empty }}
            </span>
          </div>
          <div class="col d-flex justify-content-end align-items-center">
            <span class="color-gray-6 font-16 font-weight-normal mr-2">
              Entry Status:
            </span>
            <span class="color-black font-16 font-weight-normal">
              {{ payment.voucherStatus | empty }}
            </span>
          </div>
          <div class="col-md-3" />
        </div>
        <div
          v-if="!payment.isDebtVoucher && hasDebtPurchaseOrderNumber(payment)"
          class="row"
        >
          <div class="col-3 d-flex align-items-center">
            <span class="fa-stack fa-1x color-green font-14 mr-3">
              <em class="fas fa-circle fa-stack-2x" />
              <em class="fas fa-usd-square fa-stack-1x fa-inverse" />
            </span>
            <span class="color-gray-6 font-16 font-weight-normal mr-2">
              Número de Voucher:
            </span>
            <span v-show="canEditDebtVoucherInfo">
              <validation-observer
                :ref="`voucherNumberRef-${indexPayment}`"
                novalidate
              >
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-input
                    v-model="payment.voucherNumber"
                    class="custom-input__extended w-100"
                    custom-error-msg="El voucher es requerido."
                    :error="errors[0]"
                  />
                </validation-provider>
              </validation-observer>
            </span>
            <span
              v-if="!canEditDebtVoucherInfo && payment.voucherNumber"
              class="color-black font-16 font-weight-normal"
            >
              {{ payment.voucherNumber | empty }}
            </span>
          </div>
          <div class="col d-flex justify-content-end align-items-center">
            <span class="color-gray-6 font-16 font-weight-normal mr-2">
              Fecha de Voucher:
            </span>
            <validation-observer
              :ref="`voucherDateRef-${indexPayment}`"
              novalidate
            >
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-date-picker
                  v-show="canEditDebtVoucherInfo"
                  :key="`voucher-date-${voucherDateKey}`"
                  v-model="payment.voucherDate"
                  :custom-error-msg="'La fecha de voucher es requerida.'"
                  :error="errors[0]"
                />

                <span v-if="!canEditDebtVoucherInfo && payment.voucherDate">
                  {{ payment.voucherDate | date }}
                </span>
              </validation-provider>
            </validation-observer>
          </div>
          <div class="col d-flex justify-content-end">
            <div class="d-flex align-items-center justify-content-end p-0">
              <span class="color-gray-6 font-16 font-weight-normal mr-3">
                Número de Pago:
              </span>
              <span>
                <validation-observer
                  :ref="`paymentIdFormRef-${indexPayment}`"
                  novalidate
                >
                  <validation-provider v-slot="{ errors }" rules="required">
                    <custom-input
                      v-show="canEditDebtVoucherInfo"
                      v-model="payment.paymentNumber"
                      class="custom-input__extended mr-2"
                      :custom-error-msg="'El número de pago es requerido.'"
                      :error="errors[0]"
                      maxlength="50"
                    />
                    <div>
                      <error
                        v-if="errors[0] && !isOldBilling"
                        message="El número de pago es requerido."
                        style="position: relative;top: 1.3em;right: 9em;"
                        variant="no-border"
                      />
                    </div>
                  </validation-provider>
                </validation-observer>
              </span>
              <span
                v-if="!canEditDebtVoucherInfo"
                class="color-black font-16 font-weight-normal mr-5"
              >
                {{ payment.paymentNumber | empty }}
              </span>
            </div>
          </div>
          <div class="col d-flex justify-content-end align-items-center">
            <span class="color-gray-6 font-16 font-weight-normal mr-2">
              Fecha de Pago:
            </span>
            <validation-observer
              :ref="`paymentDateRef-${indexPayment}`"
              novalidate
            >
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-date-picker
                  v-show="canEditDebtVoucherInfo"
                  v-model="payment.paymentDate"
                  :custom-error-msg="'La fecha de pago es requerida.'"
                  :error="errors[0]"
                />
                <div>
                  <error
                    v-if="errors[0] && !canEditDebtVoucherInfo"
                    message="La fecha de pago es requerida."
                    style="position: relative;top: 1.3em;right: 9em;"
                    variant="no-border"
                  />
                </div>
              </validation-provider>
            </validation-observer>
            <span
              v-if="!canEditDebtVoucherInfo"
              class="color-black font-16 font-weight-normal"
            >
              {{ payment.paymentDate | date }}
            </span>
          </div>
          <div class="col-auto d-flex align-items-end justify-content-end">
            <action-button
              v-if="
                hasDebtPurchaseOrderNumber(payment) && canEditDebtVoucherInfo
              "
              class="mb-1"
              i="fas fa-save"
              variant="outline-success"
              @click="onSaveVoucherInfo(payment, indexPayment)"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <error
              v-if="
                payment.voucherTrackingInfo &&
                  payment.voucherTrackingInfo.voucherStatusReason
              "
              :message="
                '<b>Razón de Cancelación: </b>' +
                  payment.voucherTrackingInfo.voucherStatusReason
              "
              style="border: none"
              variant="custom-error-extended"
            />
          </div>
        </div>
      </div>
      <hr />
      <div
        v-for="(item, index) in payment.billingPurchaseOrderNumbers"
        :key="`account-number-${index}`"
        class="row bg-white m-1 rounded p-3 mb-2"
      >
        <div class="col-md-4 mt-1">
          <div class="color-secondary-6 font-14">
            Número de Cifra:
          </div>
          <div class="color-black font-16">
            {{ item.fullNumber }}
          </div>
        </div>
        <div class="col-sm mt-1">
          <div class="color-secondary-6 font-14">
            Número de lí­nea:
          </div>
          <div class="color-black font-16">
            {{ item.lineNumber }}
          </div>
        </div>
        <div class="col-sm mt-1">
          <div class="color-secondary-6 font-14">
            Tipo de Cifra:
          </div>
          <div class="color-black font-16">
            {{ item.digitType }}
          </div>
        </div>
        <div class="col-sm mt-1">
          <div class="color-secondary-6 font-14">
            Grupo de edad:
          </div>
          <div class="color-black font-16">
            {{ item.ageRange }}
          </div>
        </div>
        <div class="col-sm mt-3 mt-md-1 mt-sm-3">
          <div class="color-secondary-6 font-14">
            Monto:
          </div>
          <div class="color-black font-16 font-14">
            {{ item.amount | price }}
          </div>
        </div>
        <div
          v-if="
            canRemoveDebtVoucherInfo &&
              (!payment.voucherNumber || hasDebtPurchaseOrderNumber(payment))
          "
          class="col-sm mt-1 mr-3 mr-md-0 mr-sm-3"
        >
          <action-button
            i="fas fa-times-circle"
            variant="outline-danger btn-cancel danger m-2 pr-4"
            @click="deleteBillingDigit(item)"
          >
            Eliminar
          </action-button>
        </div>
      </div>
      <div
        v-if="showBillPaymentForm(payment) && payment.isDebtVoucher"
        class="row bg-white mt-3 rounded p-2 mx-1"
      >
        <div class="col-12">
          <icon-view
            class="mb-3"
            gradient
            icon="fa fa-dollar-sign"
            icon-background="#FF7058"
            icon-color="#fff"
            title="Adjudicación por Cifra de Cuenta"
          />
        </div>
        <div class="col-md-3 mt-1">
          <div class="color-secondary-6 font-14">
            Número de Voucher:
          </div>
          <validation-observer ref="debtVoucherNumberRef" novalidate>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-input
                v-model="debtVoucherForm.voucherNumber"
                class="custom-input__extended w-100"
                custom-error-msg="El voucher es requerido."
                :disabled="!canEditVoucherInfo"
                :error="errors[0]"
              />
            </validation-provider>
          </validation-observer>
        </div>
        <div class="col-md-3 mt-1">
          <div class="color-secondary-6 font-14">
            Fecha de Voucher:
          </div>
          <validation-observer ref="debtVoucherDateRef" novalidate>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-date-picker
                v-model="debtVoucherForm.voucherDate"
                :custom-error-msg="'La fecha de voucher es requerida.'"
                :disabled="!canEditVoucherInfo"
                :error="errors[0]"
              />
            </validation-provider>
          </validation-observer>
        </div>
        <div class="col-md-3 mt-1">
          <div class="color-secondary-6 font-14">
            Número de Pago:
          </div>
          <validation-observer ref="debtPaymentNumberRef" novalidate>
            <validation-provider
              v-slot="{ errors }"
              :rules="requireDebtPaymentFields ? 'required' : ''"
            >
              <custom-input
                v-model="debtVoucherForm.paymentNumber"
                class="custom-input__extended w-100"
                custom-error-msg="El número de pago es requerido."
                :disabled="!canEditVoucherInfo"
                :error="errors[0]"
              />
            </validation-provider>
          </validation-observer>
        </div>
        <div class="col-md-3 mt-1">
          <div class="color-secondary-6 font-14">
            Fecha de Pago:
          </div>
          <validation-observer ref="debtPaymentDateRef" novalidate>
            <validation-provider
              v-slot="{ errors }"
              :rules="requireDebtPaymentFields ? 'required' : ''"
            >
              <custom-date-picker
                v-model="debtVoucherForm.paymentDate"
                :custom-error-msg="'La fecha de pago es requerida.'"
                :disabled="!canEditVoucherInfo"
                :error="errors[0]"
              />
            </validation-provider>
          </validation-observer>
        </div>
      </div>
      <validation-observer
        v-if="showBillPaymentForm(payment)"
        ref="billPaymentForm"
        novalidate
        tag="form"
      >
        <div class="row bg-white mt-3 rounded p-2 mx-1">
          <div class="col-md-4">
            <div class="color-secondary-4 font-14">
              Número de Cifra:
            </div>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="value.purchaseOrderNumber"
                class="w-75"
                :clearable="false"
                custom-error-msg="Favor seleccionar al menos una cifra de cuenta."
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                :options="paymentDigitList"
                placeholder="Seleccione"
                @input="onPurchaseOrderChange"
              />
            </validation-provider>
          </div>
          <div class="col-sm mt-1 pl-5">
            <div class="color-secondary-6 font-14">
              Número de lí­nea:
            </div>
            <div class="color-black font-16">
              {{ lineNumber }}
            </div>
          </div>
          <div class="col-sm mt-1 pl-5">
            <div class="color-secondary-6 font-14">
              Tipo de Cifra:
            </div>
            <div class="color-black font-16">
              {{ digitType }}
            </div>
          </div>
          <div v-if="!payment.isDebtVoucher" class="col-sm mt-1 pl-5">
            <div class="color-secondary-6 font-14">
              Grupo de Edad:
            </div>
            <div class="color-black font-16">
              {{ billingServiceCategoryName }}
            </div>
          </div>
          <div
            v-if="!payment.isDebtVoucher"
            class="col-sm mt-3 mt-md-1 mt-sm-3"
          >
            <div class="color-secondary-6 font-14">
              Balance:
            </div>
            <div class="color-secondary-6font-14">
              {{ balance | price }}
            </div>
          </div>
          <div class="col-sm mt-3 mr-4 mt-md-1 mt-sm-3">
            <div class="color-secondary-6 font-14">
              Monto:
            </div>
            <validation-provider v-slot="{ errors }" rules="required">
              <div class="input-group mb-3">
                <custom-input
                  v-model="value.amount"
                  class="custom-input__extended w-100"
                  custom-error-msg="Campo Requerido"
                  :error="errors[0]"
                  mask-type="Money"
                  prepend-icon="fa fa-dollar-sign"
                  :prepend-icon-left="true"
                  prepend-icon-type="success"
                />
                <error
                  v-if="errors[0]"
                  message="Favor de entrar el monto. (0326)."
                  variant="no-border"
                />
              </div>
            </validation-provider>
          </div>
          <div
            class="col-md-12 mt-1 d-flex align-items-center justify-content-end"
          >
            <action-button
              i="fas fa-times-circle"
              variant="outline-danger btn-cancel danger"
              @click="cancelBillingDigit()"
            >
              Cancelar
            </action-button>
            <action-button
              i="fas fa-save"
              variant="success m-2 pl-4 pr-4"
              @click="saveBillingDigit(payment)"
            >
              Guardar
            </action-button>
          </div>
        </div>

        <error
          v-if="!isValidAmountForGrant && !payment.isDebtVoucher"
          class="pt-2 ml-2"
          is-alert-mode
          message="El monto ingresado supera el disponible en el detalle de la factura para este grupo. Te recomendamos verificar e ingresar un monto válido."
        />
      </validation-observer>
      <div class="row ml-2 mt-3">
        <div
          v-if="showProof(payment)"
          class="col-md-3 d-flex align-items-center"
        >
          <span class="color-secondary-6 font-14 mr-2">
            Número de Voucher:
          </span>
          <span class="color-black font-16">
            {{ payment.voucherNumber }}
          </span>
        </div>
        <div
          v-if="showProof(payment)"
          class="col-md-3 d-flex align-items-center"
        >
          <span class="color-secondary-6 font-14 mr-2">
            Número de Cheque:
          </span>
          <span class="color-black font-16">
            {{ payment.paymentIdReference }}
          </span>
        </div>
        <div
          v-if="
            !isPayment(payment) &&
              !payment.voucherNumber &&
              !payment.isDebtVoucher
          "
          :class="
            isPayment(payment) && !payment.voucherNumber
              ? 'col-md-6 text-right mt-1'
              : 'col-md-12 text-right mt-1'
          "
        >
          <action-button
            i="fas fa-times-circle"
            variant="outline-danger btn-cancel danger mt-3"
            @click="cancelPartialPayment(payment)"
          >
            Cancelar Pago Parcial
          </action-button>
          <action-button
            i="fas fa-file-plus"
            variant="outline-success m-2 pl-3 pr-3"
            @click="addPayment(payment)"
          >
            Añadir Cifra
          </action-button>
        </div>
      </div>
      <div v-if="showTrackingInfo(payment)" class="mt-4 bg-gray-9 rounded p-3">
        <voucher-and-payment-status-info :info="payment.voucherTrackingInfo" />
      </div>
      <slot v-bind="{ payment }" name="documents" />
    </div>
    <callout
      v-if="billInfo.payments.length == 0 && !isApprovedStatus"
      border-color="secondary-2"
      class-main="py-3"
    >
      <div class="row justify-content-center">
        <div class="col-12 col-lg-3 ml-lg-n4 align-self-center">
          <span class="no-info-text">No hay información disponible.</span>
        </div>
      </div>
    </callout>
    <div v-if="!hasActiveDebtVoucher" class="d-flex justify-content-end mt-3">
      <div>
        <action-button
          v-if="canAddVoucher"
          i="fas fa-file-plus"
          text="Añadir Voucher"
          variant="success"
          @click="onAddVoucherClick"
        />
      </div>
      <div class="ml-3">
        <action-button
          v-if="canAddDebtVoucher"
          i="fas fa-file-plus"
          text="Documentar Voucher de Deuda"
          variant="success"
          @click="addDebtVoucherPayment"
        />
      </div>
    </div>
  </div>
</template>

<script>
import IconView from 'icon-view';
import CustomDropDown from 'custom-drop-down';
import CustomDatePicker from 'custom-date-picker';
import ActionButton from 'ActionButton';
import VoucherAndPaymentStatusInfo from './VoucherAndPaymentStatusInfo.vue';
import alert from 'utils/mixins/alert.mixin';
import billingStatus from '@/utils/constants/billingStatus.js';
import {
  getDigitNumbers,
  getDebtDigitNumbers,
  saveVoucherInfo as _saveVoucherInfo,
} from '@/modules/billingHistory/api';
import roles from '@/utils/constants/roles.js';
import hiringTypes from '@/utils/constants/hiringTypes.js';
import voucherStatus from '@/utils/constants/voucherStatus';
import CustomInput from 'CustomInput';
import Error from 'custom-error';
export default {
  name: 'PartialAccountNumber',
  components: {
    IconView,
    CustomDropDown,
    CustomDatePicker,
    ActionButton,
    VoucherAndPaymentStatusInfo,
    CustomInput,
    Error,
  },
  mixins: [alert],
  props: {
    value: {
      type: Object,
      default: () => {
        return {
          //Only related to Forms
          purchaseOrderNumberId: 0,
          purchaseOrderNumber: null,
          amount: 0,
          billId: 0,
          isPartial: true,
        };
      },
    },
    billInfo: {
      type: Object,
      default: () => {
        return {
          payments: [],
          billingPurchaseOrderNumbers: [],
          totalPurchase: 0,
          BillTotal: 0,
          addDigitForm: false,
        };
      },
    },
    billStatus: {
      type: Number,
      default: 0,
    },
    billTotalAmount: {
      type: Number,
      default: 0,
    },
    purchaseOrders: {
      type: Array,
      default: () => [],
    },

    showUpdateVoucherStatus: {
      type: Boolean,
      default: () => false,
    },
    billId: {
      type: Number,
      default: null,
    },
    distributionInfo: {
      type: Object,
      default: () => {},
    },
    canSaveVoucherInfo: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    digitNumbers: [],
    balance: 0,
    lineNumber: null,
    billingServiceCategoryName: null,
    digitType: null,
    debtVoucherForm: {
      voucherNumber: null,
      voucherDate: null,
      paymentNumber: null,
      paymentDate: null,
    },
    debtHiringTypeId: hiringTypes.debt,
  }),
  computed: {
    hasDebtPurchaseOrderNumber() {
      return (payment) =>
        payment.billingPurchaseOrderNumbers?.some(
          (d) => d.hiringType === hiringTypes.debt
        );
    },
    canAddMoreVoucher() {
      return (
        this.billStatus === billingStatus.Aprobado &&
        !this.billInfo.payments.some((p) =>
          p.billingPurchaseOrderNumbers?.some(
            (d) => d.hiringType === hiringTypes.debt
          )
        )
      );
    },
    hasActiveDebtVoucher() {
      return this.billInfo.payments.some((p) => p.isDebtVoucher);
    },
    visiblePayments() {
      return this.billInfo.payments.filter(
        (p) => !this.hasActiveDebtVoucher || p.isDebtVoucher
      );
    },
    requireDebtPaymentFields() {
      const remaining = this.billTotalAmount - this.totalPurchase;
      return this.value.amount < remaining;
    },
    voucherDateKey() {
      // Generar una key única basada en la fecha para forzar re-render
      if (!this.billInfo.voucherDate) return 'empty';
      const date =
        this.billInfo.voucherDate instanceof Date
          ? this.billInfo.voucherDate
          : new Date(this.billInfo.voucherDate);
      return isNaN(date.getTime()) ? 'invalid' : date.getTime().toString();
    },
    canRemoveDebtVoucherInfo() {
      return [
        billingStatus.Aprobado,
        billingStatus.EnviadoFinanzasParcial,
      ].includes(this.billStatus);
    },
    canEditDebtVoucherInfo() {
      return [
        billingStatus.EnviadoFinanzas,
        billingStatus.EnviadoFinanzasParcial,
      ].includes(this.billStatus);
    },
    canEditVoucherInfo() {
      return [
        billingStatus.EnviadoFinanzas,
        billingStatus.EnviadoFinanzasParcial,
        billingStatus.DevueltoTPFA,
        billingStatus.Aprobado,
      ].includes(this.billStatus);
    },
    isValidAmountForGrant() {
      if (
        !this.value.purchaseOrderNumber ||
        this.value.purchaseOrderNumber.digitType.trim().toLowerCase() ===
          'estatal'
      ) {
        return true;
      }

      const distributions = [
        'appointmentDistribution',
        'planDistribution',
        'tapRequestDistribution',
        'visitDistribution',
        'consultingDistribution',
      ];

      const totalAmount = distributions.reduce((sum, distKey) => {
        const distributionItem = this.distributionInfo[distKey]?.find(
          (item) =>
            item.billingServiceCategoryId ===
            this.value.purchaseOrderNumber?.billingServiceCategoryId
        );
        return sum + (distributionItem ? distributionItem.amount : 0);
      }, 0);

      return totalAmount >= this.value.amount;
    },
    paymentDigitList() {
      return this.digitNumbers
        .filter(
          (f) =>
            !this.billInfo.payments.some(
              (p) =>
                p.billingPurchaseOrderNumbers != undefined &&
                p.billingPurchaseOrderNumbers.some(
                  (s) =>
                    s.lineNumber == f.lineNumber &&
                    s.purchaseOrderNumberId == f.purchaseOrderNumberId &&
                    p.paymentStatus == '' &&
                    s.lineStatus !== 'C'
                )
            ) &&
            !(this.billInfo.billingPurchaseOrderNumbers || []).some(
              (s) =>
                s.lineNumber == f.lineNumber &&
                s.purchaseOrderNumberId == f.purchaseOrderNumberId
            )
        )
        .sort((a, b) => a.lineNumber - b.lineNumber);
    },
    isApprovedStatus() {
      return billingStatus.Aprobado == this.billStatus;
    },
    totalPurchase() {
      let total = 0;
      this.billInfo.payments
        .filter((x) => x.voucherStatus != voucherStatus.Cancelled)
        .map((item) => {
          if (item.billingPurchaseOrderNumbers != undefined) {
            item.billingPurchaseOrderNumbers.forEach((element) => {
              total += element.amount;
            });
          }
        });
      return total;
    },
    canAddMorePayments() {
      return this.billStatus == billingStatus.EnviadoFinanzasParcial;
    },
    isPurchaseOrdersEmpty() {
      return (
        this.billInfo.billingPurchaseOrderNumbers.length == 0 &&
        !this.billInfo.addDigitForm &&
        this.isApprovedStatus
      );
    },
    showChoosePO() {
      return (payment) => !payment.purchaseOrderId;
    },
    showTrackingInfo() {
      return (payment) => payment.voucherNumber && payment.voucherTrackingInfo;
    },
    showBillPaymentForm() {
      return (payment) =>
        this.billInfo.addDigitForm &&
        payment.showForm &&
        !this.isPayment(payment);
    },
    canAddVoucher() {
      let canAdd =
        this.billInfo.payments.filter(
          (f) => f.voucherNumber === null || f.voucherNumber === undefined
        ).length == 0 &&
        [
          billingStatus.EnviadoFinanzasParcial,
          billingStatus.DevueltoTPFA,
          billingStatus.Aprobado,
        ].includes(this.billStatus) &&
        ![roles.Provider, roles.ProviderCPA].includes(this.userRole);
      return canAdd;
    },
    canAddDebtVoucher() {
      return (
        this.billInfo.payments.filter(
          (f) => f.voucherNumber === null || f.voucherNumber === undefined
        ).length == 0 &&
        [
          billingStatus.EnviadoFinanzasParcial,
          billingStatus.DevueltoTPFA,
          billingStatus.Aprobado,
        ].includes(this.billStatus) &&
        this.userHasPermissions(
          'provider.billing.payment.document-debt-voucher'
        )
      );
    },
    cantPrintDistributionNumber() {
      return this.userHasPermissions(
        'provider.billing.printdistributionnumber'
      );
    },
  },
  watch: {
    'billInfo.payments'(newValue) {
      if (newValue != undefined) {
        this.billInfo.totalPurchase = this.totalPurchase; //For gears
      }
    },
    'billInfo.addDigitForm'(formState) {
      if (formState) {
        this.clearForm();
      }
    },
  },
  mounted() {
    this.billInfo.totalPurchase = this.totalPurchase; //For gears
  },
  methods: {
    showProof(item) {
      return [billingStatus.Pagado, billingStatus.PagadoParcial].includes(
        item.paymentStatusId
      );
    },
    isPayment(item) {
      return [
        billingStatus.Pagado,
        billingStatus.EnviadoFinanzasParcial,
      ].includes(item.paymentStatusId);
    },
    async validateBill(payment) {
      let isValid;
      try {
        isValid = await this.$refs.billPaymentForm[0].validate();
      } catch {
        isValid = await this.$refs.billPaymentForm.validate();
      }
      if (!isValid) {
        this.ShowToast('', 'Debe completar los campos requeridos.', 'error');
        return;
      }

      if (this.value.amount <= 0) {
        this.ShowToast('', 'El monto debe ser mayor que cero.', 'error');
        return;
      }

      if (!payment?.isDebtVoucher && this.value.amount > this.balance) {
        this.ShowToast(
          '',
          'El monto no puede sobrepasar el balance de la cifra seleccionada.',
          'error'
        );
        return;
      }

      if (this.value.amount > this.billTotalAmount) {
        this.ShowToast(
          '',
          'El monto no puede sobrepasar el balance total de la factura.',
          'error'
        );
        return;
      }

      const subtotal = this.billTotalAmount - this.totalPurchase;

      if (this.value.amount > subtotal.toFixed(2)) {
        this.ShowToast(
          '',
          'El monto no puede sobrepasar el balance total de la factura.',
          'error'
        );
        return;
      }
      if (
        payment &&
        payment.billingPurchaseOrderNumbers &&
        payment.billingPurchaseOrderNumbers.some(
          (bpo) =>
            bpo.digitType.trim().toLowerCase() !==
            this.value.purchaseOrderNumber.digitType.trim().toLowerCase()
        )
      ) {
        const type = {
          federal: 'estatal',
          estatal: 'federal',
        };
        this.ShowToast(
          '',
          `No es posible seleccionar una cifra ${this.value.purchaseOrderNumber.digitType.toLowerCase()}, ya que cuenta con una cifra ${
            type[this.value.purchaseOrderNumber.digitType.toLowerCase()]
          }.`,
          'error'
        );
        return;
      }
      return isValid;
    },
    async saveBillingDigit(payment) {
      if (payment.isDebtVoucher) {
        const isValid = await this.validateDebtVoucherFields();
        if (!isValid) {
          this.ShowToast('', 'Debe completar los campos requeridos.', 'error');
          return;
        }
      }
      if (await this.validateBill(payment)) {
        this.value.lineNumber = this.lineNumber;
        if (payment.isDebtVoucher) {
          this.value.isDebtVoucher = true;
          this.value.voucherNumber = this.debtVoucherForm.voucherNumber;
          this.value.voucherDate = this.debtVoucherForm.voucherDate;
          this.value.paymentNumber = this.debtVoucherForm.paymentNumber;
          this.value.paymentDate = this.debtVoucherForm.paymentDate;
        }
        this.$emit('saveBillingDigit');
        this.billInfo.totalPurchase = this.totalPurchase;
      }
    },

    async validateDebtVoucherFields() {
      const validate = async (refName) => {
        const ref = this.$refs[refName];
        if (!ref) return true;
        const target = Array.isArray(ref) ? ref[0] : ref;
        return target ? await target.validate() : true;
      };
      const results = await Promise.all([
        validate('debtVoucherNumberRef'),
        validate('debtVoucherDateRef'),
        ...(this.requireDebtPaymentFields
          ? [validate('debtPaymentNumberRef'), validate('debtPaymentDateRef')]
          : []),
      ]);
      return results.every(Boolean);
    },

    deleteBillingDigit(item) {
      this.value.purchaseOrderId = null;
      this.$emit('deleteBillingDigit', item);
    },

    async addDebtVoucherPayment(item) {
      this.addPayment(item);
      const active = this.billInfo.payments.find((p) => p.showForm);
      if (active) active.isDebtVoucher = true;

      await this.loadDebtDigitNumbers();
    },

    async loadDebtDigitNumbers() {
      const { data: response } = await getDebtDigitNumbers(
        this.billId,
        this.debtHiringTypeId
      );
      this.digitNumbers = (response ?? []).filter((t) => t.lineStatus !== 'C');
    },

    async onAddVoucherClick(item) {
      const activeDebt = this.billInfo.payments.find((p) => p.isDebtVoucher);
      if (activeDebt) {
        const answer = await this.ShowCancelModal(
          'Confirmación',
          'La información documentada por el voucher de deuda será eliminada.',
          'Sí',
          'No'
        );
        if (!answer) return;
        this.clearDebtVoucherForm(activeDebt);
      }
      this.addPayment(item);
    },

    clearDebtVoucherForm(payment) {
      if (payment) payment.isDebtVoucher = false;
      this.debtVoucherForm = {
        voucherNumber: null,
        voucherDate: null,
        paymentNumber: null,
        paymentDate: null,
      };
    },
    addPayment(item) {
      //Set NewLine _blank
      this.billInfo.addDigitForm = false;
      if (
        item == undefined ||
        this.billInfo.payments.every((p) => p.voucherNumber)
      ) {
        this.billInfo.payments.forEach((p) => (p.showForm = false));
        this.value.paymentId = null;
        this.billInfo.payments.push({
          purchaseTotal: 0,
          showForm: true,
        });
      } else if (item != undefined) {
        this.value.paymentId = item.paymentId;

        const poid = this.purchaseOrders.find(
          (x) => x.poNumber == item.purchaseOrderId
        );

        this.value.purchaseOrderId = poid;
        this.onPOIDChange();
        if (this.billInfo.addDigitForm && item.showForm) {
          if (this.validateBill(item)) {
            this.ShowToast(
              '',
              'Para añadir el pago parcial debe oprimir guardar.',
              'error'
            );
            return;
          }
        }
        this.billInfo.payments.forEach((p) => (p.showForm = false));
        item.showForm = true;
      }
      this.billInfo.addDigitForm = true;
    },
    cancelPartialPayment(payment) {
      //Cancel Partial Payment
      this.value.purchaseOrderId = null;
      if (
        payment.billingPurchaseOrderNumbers == undefined &&
        this.billInfo.payments.length == 1
      ) {
        this.billInfo.payments.pop();
        this.billInfo.addDigitForm = false;
        this.billInfo.isPartialPayment = false;
      } else {
        if (payment.billingPurchaseOrderNumbers == undefined) {
          this.billInfo.payments.pop();
          this.billInfo.addDigitForm = false;
        } else {
          this.$emit('cancelPartialPayment', payment);
        }
      }
    },
    cancelBillingDigit() {
      //Only is first payment
      this.value.purchaseOrderId = null;
      const debtIndex = this.billInfo.payments.findIndex(
        (p) => p.isDebtVoucher
      );
      if (debtIndex !== -1) {
        this.billInfo.payments.splice(debtIndex, 1);
        this.clearDebtVoucherForm();
        this.billInfo.addDigitForm = false;
        return;
      }
      if (
        this.billInfo.payments?.length == 1 &&
        this.billInfo.payments.some((p) => p.totalPurchase == 0)
      ) {
        this.billInfo.payments.pop();
      }
      this.billInfo.addDigitForm = false;
    },
    onPurchaseOrderChange(value) {
      this.balance = value.doeBalanceAmount;
      this.value.amount = value.merchandiseAmount;
      this.lineNumber = value.lineNumber;
      this.digitType = value.digitType;
      this.billingServiceCategoryName = value.billingServiceCategoryName;
    },
    clearForm() {
      this.value.purchaseOrderNumber = null;
      this.value.purchaseOrderNumberId = null;
      this.balance = null;
      this.lineNumber = null;
      this.digitType = null;
      this.billingServiceCategoryName = null;
    },
    async onChangePurchaseOrderHandler(value) {
      if (value?.id) {
        this.$emit('on-select-purchase-order', value?.id);
      }
      if (this.value.purchaseOrderNumber != null) {
        await this.ShowCancelModal(
          'Confirmación',
          'Usted cuenta con información documentada en sistema. ¿Está seguro que desea eliminar la información para cambiar el PO ID?',
          'Sí­',
          'No'
        ).then(async (answer) => {
          if (!answer) return;
          this.onPOIDChange();
        });
      } else {
        this.onPOIDChange();
      }
    },
    async onPOIDChange() {
      let poid = this.value?.purchaseOrderId;
      this.digitNumbers = [];

      let { data: response } = await getDigitNumbers(poid.id, this.billId);
      if (response.length <= 0) {
        this.clearForm();
      }
      this.digitNumbers = response.filter((t) => t.lineStatus !== 'C');
    },
    async validateVoucherRequiredFieldsAsync(indexPayment) {
      const validate = async (refName) => {
        const ref = this.$refs[`${refName}-${indexPayment}`];
        const target = Array.isArray(ref) ? ref[0] : ref;
        return target ? await target.validate() : true;
      };

      const results = await Promise.all([
        validate('voucherNumberRef'),
        validate('voucherDateRef'),
        validate('paymentIdFormRef'),
        validate('paymentDateRef'),
      ]);

      return results.every(Boolean);
    },
    async onSaveVoucherInfo(payment, indexPayment) {
      const isValid = await this.validateVoucherRequiredFieldsAsync(
        indexPayment
      );
      if (!isValid) {
        return;
      }

      const payload = {
        billId: this.billId,
        voucherNumber: payment.voucherNumber,
        voucherDate: payment.voucherDate,
        paymentId: payment.paymentId,
        paymentDate: payment.paymentDate,
        paymentNumber: payment.checkNumber,
      };

      await _saveVoucherInfo(payload)
        .then((response) => {
          if (response.data) {
            this.ShowToast('', 'Voucher guardado exitosamente.', 'success');
            this.$emit('refresh-details');
          }
        })
        .catch(() => {
          this.ShowToast('', 'Error al guardar el voucher.', 'error');
        });
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/shared/scss/config/colors.scss';
::v-deep .custom-input__extended {
  .input-group {
    .input-group-text {
      background: map-get($colors, 'green');
      color: map-get($colors, 'white');
    }
  }
}

::v-deep .custom-error-extended {
  p {
    color: #000 !important;
  }
}
</style>
